import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPhone, faGlobe, faClock, faMapMarkerAlt, faThumbsUp, faDollarSign, faWrench, faArrowRight,
} from '@fortawesome/free-solid-svg-icons'

import services from '../../../services/api'
import { getSafeSiteUrl } from '../../../utils/safeUrl'
import { softCase } from './List'
import StarRating from './StarRating'
import { formatPhone, formatPercent, formatHour, detailsPath, surveyMetrics } from './companyFormat'

const PANEL_PHOTOS = 3

// Detalhe resumido da oficina escolhida, ao lado da lista (desktop).
// O completo (fotos, mapa e avaliacoes) fica na pagina de detalhe.
export default function CompanyPanel({ selected }) {
  const [state, setState] = useState({ loading: true, error: false, company: null, ratings: null })

  useEffect(() => {
    if (!selected) return undefined
    let active = true
    setState({ loading: true, error: false, company: null, ratings: null })

    const load = async () => {
      try {
        const { data } = await services.api.get(
          services.config.endpoint + `companies/get-data-provider/${selected.sourceId}/${selected.id}`
        )
        // Notas publicas calculadas das avaliacoes; falha aqui nao derruba o painel.
        // null = sem avaliacoes carregadas: o bloco de notas nao aparece (zero pareceria nota ruim).
        let ratings = null
        if (data && data.cpfCnpj) {
          try {
            const survey = await services.api.get(
              services.config.endpoint + `satisfaction-survey/by-company-source/${data.cpfCnpj}/${selected.sourceId}`
            )
            const evaluations = survey.data && survey.data.avaliacoes
            ratings = evaluations && evaluations.length ? surveyMetrics(evaluations) : null
          } catch (err) {
            ratings = null
          }
        }
        if (active) setState({ loading: false, error: false, company: data, ratings })
      } catch (err) {
        // Sem os dados, o painel avisa e oferece a pagina completa.
        if (active) setState({ loading: false, error: true, company: null, ratings: null })
      }
    }

    load()
    return () => { active = false }
  }, [selected])

  if (!selected) {
    return (
      <aside className='cp-panel cp-panel-empty'>
        <p>Escolha uma oficina na lista para ver os detalhes aqui.</p>
      </aside>
    )
  }

  const { loading, error, company, ratings } = state

  if (loading) {
    return (
      <aside className='cp-panel' aria-busy='true' aria-label='Carregando detalhes da oficina'>
        <span className='cp-skeleton cp-skeleton-name' />
        <span className='cp-skeleton cp-skeleton-meta' />
        <span className='cp-skeleton cp-skeleton-address' />
        <span className='cp-skeleton cp-panel-skeleton-block' />
      </aside>
    )
  }

  if (error || !company) {
    return (
      <aside className='cp-panel cp-panel-empty'>
        <p>Não foi possível carregar os detalhes agora.</p>
        <Link className='cp-panel-link' to={detailsPath(selected)}>
          Abrir a página da empresa <FontAwesomeIcon icon={faArrowRight} aria-hidden='true' />
        </Link>
      </aside>
    )
  }

  const reviews = Number(company.amountOfSatisfactionSurvey) || 0
  // "00:00 às 00:00" e horario nao cadastrado, nao loja aberta 24h.
  const hasHours = [company.openAt, company.closesAt].some(h => h && formatHour(h) !== '00:00')
  const phone = formatPhone(company.Phones)
  const site = getSafeSiteUrl(company.site)
  const photos = (company.Files || []).filter(f => f && f.file).slice(0, PANEL_PHOTOS)
  const services_ = company.Branches && company.Branches.description === 'Autopeças'
    ? [{ id: 'pecas', description: 'Venda de peças' }]
    : company.Services || []
  const address = [
    [softCase(company.street), company.streetNumber].filter(Boolean).join(', '),
    softCase(company.neighborhood),
    [softCase(company.city), company.state].filter(Boolean).join(' - '),
  ].filter(Boolean).join(' · ')

  return (
    <aside className='cp-panel' aria-labelledby='cp-panel-title'>
      <header className='cp-panel-head'>
        {company.Branches && company.Branches.description &&
          <span className='cp-panel-branch'>{company.Branches.description}</span>}
        <h2 id='cp-panel-title'>{softCase(company.tradingName || company.name)}</h2>
        <p className='cp-panel-rating'>
          {reviews > 0
            ? <><StarRating value={company.rating} size='md' /> <span>{reviews} {reviews === 1 ? 'avaliação' : 'avaliações'}</span></>
            : 'Sem avaliações ainda'}
        </p>
      </header>

      {photos.length > 0 &&
        <ul className='cp-panel-photos'>
          {photos.map((photo, index) => (
            <li key={photo.file + index}><img src={photo.file} alt='' loading='lazy' /></li>
          ))}
        </ul>
      }

      <ul className='cp-panel-facts'>
        <li><FontAwesomeIcon icon={faMapMarkerAlt} aria-hidden='true' /><span>{address}</span></li>
        {phone &&
          <li>
            <FontAwesomeIcon icon={faPhone} aria-hidden='true' />
            <a href={`tel:+55${phone.replace(/\D/g, '')}`}>{phone}</a>
          </li>
        }
        {hasHours &&
          <li>
            <FontAwesomeIcon icon={faClock} aria-hidden='true' />
            <span>Funciona das {formatHour(company.openAt)} às {formatHour(company.closesAt)}</span>
          </li>
        }
        {site &&
          <li>
            <FontAwesomeIcon icon={faGlobe} aria-hidden='true' />
            <a href={site} target='_blank' rel='noopener noreferrer'>Acessar o site</a>
          </li>
        }
      </ul>

      {ratings &&
        <dl className='cp-panel-metrics'>
          <div>
            <dt><FontAwesomeIcon icon={faThumbsUp} aria-hidden='true' />Indicaria</dt>
            <dd>{formatPercent(ratings.ratingRecommendation)}</dd>
          </div>
          <div>
            <dt><FontAwesomeIcon icon={faDollarSign} aria-hidden='true' />Preço</dt>
            <dd><StarRating value={ratings.ratingPrice} /></dd>
          </div>
          <div>
            <dt><FontAwesomeIcon icon={faWrench} aria-hidden='true' />Serviço</dt>
            <dd><StarRating value={ratings.ratingProductService} /></dd>
          </div>
        </dl>
      }

      {company.Segments && company.Segments.length > 0 &&
        <section className='cp-panel-section'>
          <h3>Veículos atendidos</h3>
          <p>{company.Segments.map(s => s.description).join(' · ')}</p>
        </section>
      }

      {services_.length > 0 &&
        <section className='cp-panel-section'>
          <h3>Serviços</h3>
          <p>{services_.map(s => s.description).join(' · ')}</p>
        </section>
      }

      <Link className='cp-panel-link' to={{ pathname: detailsPath(selected), state: { fromSearch: true } }}>
        Ver página completa, com fotos, mapa e avaliações
        <FontAwesomeIcon icon={faArrowRight} aria-hidden='true' />
      </Link>
    </aside>
  )
}
